import { createContext, useReducer, useCallback } from "react";

import axios from "../../utils/axios/v1/userAxios";

const GET_LLM_CONFIG = "GET_LLM_CONFIG";
const GET_LLM_PROMPT_SCANNERS = "GET_LLM_PROMPT_SCANNERS";
const GET_LLM_OUTPUT_SCANNERS = "GET_LLM_OUTPUT_SCANNERS";

const SET_ERROR = "SET_ERROR";

const initialState = {
  llmConfig: null,
  llmPromptScanners: null,
  llmOutputScanners: null,

  errMsg: null,
};

const LLMConfigReducer = (state, action) => {
  switch (action.type) {
    case GET_LLM_CONFIG:
      return {
        ...state,
        llmConfig: action.payload.llmConfig,
      };
    case GET_LLM_PROMPT_SCANNERS:
      return {
        ...state,
        llmPromptScanners: action.payload.llmPromptScanners,
      };
    case GET_LLM_OUTPUT_SCANNERS:
      return {
        ...state,
        llmOutputScanners: action.payload.llmOutputScanners,
      };
    case SET_ERROR:
      return {
        ...state,
        errMsg: action.payload.errMsg,
      };
    default:
      return state;
  }
};

const LLMConfigContext = createContext(null);

function LLMConfigProvider({ children }) {
  const [state, dispatch] = useReducer(LLMConfigReducer, initialState);

  const setErr = useCallback((msg) => {
    dispatch({
      type: SET_ERROR,
      payload: {
        errMsg: msg,
      },
    });
  }, []);
  const setLLMConfig = useCallback((llmConfig) => {
    dispatch({
      type: GET_LLM_CONFIG,
      payload: {
        llmConfig: llmConfig,
      },
    });
  }, []);

  const getLLMConfig = useCallback(
    async (zoneUid, init = false) => {
      if (init) {
        dispatch({
          type: GET_LLM_CONFIG,
          payload: {
            llmConfig: null,
          },
        });
      }
      try {
        const response = await axios.get(`/config/llm/${zoneUid}`);
        dispatch({
          type: GET_LLM_CONFIG,
          payload: {
            llmConfig: response.data,
          },
        });
      } catch (err) {
        dispatch({
          type: GET_LLM_CONFIG,
          payload: {
            llmConfig: [],
          },
        });
        setErr(err.message);
      }
    },
    [setErr]
  );

  const configLLMSetting = useCallback(
    async (zoneUid, name, value) => {
      try {
        let response;
        switch (name) {
          case "change_active":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              active: value.enable,
            });
            break;
          case "requestScanActiveChange":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              prompt_scan_active: value.prompt_scan_active,
            });
            break;
          case "responseScanActiveChange":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              output_scan_active: value.output_scan_active,
            });
            break;
          case "fallbackEnableChange":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              fallback: { enabled: value.fallback_enable },
            });
            break;
          case "fallbackRetryAttemptsChange":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              fallback: { retry_attempts: value.fallback_retry_attempts },
            });
            break;
          case "fallbackRetryIntervalChange":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              fallback: { retry_interval: value.fallback_retry_interval },
            });
            break;
          case "fallbackUrlsChange":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              fallback: { url1: value.fallback1, url2: value.fallback2 },
            });
            break;
          case "customBlockPageChange":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              block_page: value.content,
            });
            break;
          case "set_block_page":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              block_page: value,
            });
            break;
          case "requestPayloadChange":
            response = await axios.patch(`/config/log/${zoneUid}/llm_audit_log_config`, value);
            break;
          case "useCacheChange":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              use_cache: value.use_cache,
            });
            break;
          case "promptOutputFilters":
            response = await axios.patch(`/config/llm/${zoneUid}`, {
              llm_provider_type: value.type,
              prompt_filter: value.prompt_filter,
              output_filter: value.output_filter,
            });
            break;
          case "revokeAPIToken":
            response = await axios.patch(`/config/llm/${zoneUid}/token`, {
              jti: value.jti,
            });
            break;
          default:
            break;
        }
        if (response) {
          setLLMConfig(response.data);
        }
      } catch (err) {
        setErr(err.message);
        getLLMConfig(zoneUid, true);
      }
    },
    [setErr, setLLMConfig, getLLMConfig]
  );

  const createAPIToken = useCallback(
    async (zoneUid, value) => {
      try {
        const response = await axios.post(`/config/llm/${zoneUid}/token`, {
          name: value.name,
          expires_in: value.expires_in,
        });
        if (!response) {
          setErr("Invalid response");
          getLLMConfig(zoneUid, true);
        } else {
          setLLMConfig(response.data?.cfg);
          return response.data?.token;
        }
      } catch (err) {
        setErr(err.message);
        getLLMConfig(zoneUid, true);
      }
      return false;
    },
    [setErr, setLLMConfig, getLLMConfig]
  );

  const setLLMPromptScanners = useCallback((llmPromptScanners) => {
    dispatch({
      type: GET_LLM_PROMPT_SCANNERS,
      payload: {
        llmPromptScanners: llmPromptScanners,
      },
    });
  }, []);

  const getLLMPromptScanners = useCallback(
    async (zoneUid, init = false) => {
      if (init) {
        setLLMPromptScanners(null);
      }
      try {
        const response = await axios.get(`/config/llm/${zoneUid}/prompt_scanners`);
        setLLMPromptScanners(response.data);
      } catch (err) {
        setLLMPromptScanners([]);
        setErr(err.message);
      }
    },
    [setErr, setLLMPromptScanners]
  );

  const configLLMSettingPromptScanners = useCallback(
    async (zoneUid, name, value) => {
      try {
        const response = await axios.patch(`/config/llm/${zoneUid}/prompt_scanners`, {
          scanner_name: name,
          data: value,
        });
        if (response) {
          setLLMPromptScanners(response.data);
        }
      } catch (err) {
        setErr(err.message);
        getLLMPromptScanners(zoneUid, true);
      }
    },
    [setErr, setLLMPromptScanners, getLLMPromptScanners]
  );

  const setLLMOutputScanners = useCallback((llmOutputScanners) => {
    dispatch({
      type: GET_LLM_OUTPUT_SCANNERS,
      payload: {
        llmOutputScanners: llmOutputScanners,
      },
    });
  }, []);

  const getLLMOutputScanners = useCallback(
    async (zoneUid, init = false) => {
      if (init) {
        setLLMOutputScanners(null);
      }
      try {
        const response = await axios.get(`/config/llm/${zoneUid}/output_scanners`);
        setLLMOutputScanners(response.data);
      } catch (err) {
        setLLMOutputScanners([]);
        setErr(err.message);
      }
    },
    [setErr, setLLMOutputScanners]
  );

  const configLLMSettingOutputScanners = useCallback(
    async (zoneUid, name, value) => {
      try {
        const response = await axios.patch(`/config/llm/${zoneUid}/output_scanners`, {
          scanner_name: name,
          data: value,
        });
        if (response) {
          setLLMOutputScanners(response.data);
        }
      } catch (err) {
        setErr(err.message);
        getLLMOutputScanners(zoneUid, true);
      }
    },
    [setErr, setLLMOutputScanners, getLLMOutputScanners]
  );

  // const getEdgeConfig = useCallback(
  //   async () => {
  //     try {
  //       const response = await axios.get("/config/llm/edge");
  //       return response.data;
  //     } catch (err) {
  //       setErr(err.message);
  //       return {};
  //     }
  //   },
  //   [setErr]
  // );

  return (
    <LLMConfigContext.Provider
      value={{
        ...state,
        setErr,
        getLLMConfig,
        configLLMSetting,
        createAPIToken,
        // getEdgeConfig,
        getLLMPromptScanners,
        getLLMOutputScanners,
        configLLMSettingPromptScanners,
        configLLMSettingOutputScanners,
      }}
    >
      {children}
    </LLMConfigContext.Provider>
  );
}

export { LLMConfigContext, LLMConfigProvider };
